import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Grid, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PeopleIcon from '@mui/icons-material/People';

const StatCard = ({ icon, title, value, color, tooltip, isSelected, onClick }) => {
  const card = (
    <Paper 
      elevation={isSelected ? 4 : 2}
      onClick={onClick}
      sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        borderTop: `4px solid ${color}`,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
        '&:hover': {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        },
        '&::after': isSelected ? {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '4px',
          backgroundColor: color,
        } : {}
      }}
    >
      <Box sx={{ color, mb: 1 }}>
        {icon}
      </Box>
      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        {title}
      </Typography>
    </Paper>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      {card}
    </Tooltip>
  ) : card;
};

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func
};

const MappingSummary = ({ 
  totalEmployees, 
  mappedEmployees, 
  unmappedEmployees, 
  selectedFilter, 
  onFilterChange 
}) => {
  const mappedPercentage = totalEmployees > 0 
    ? Math.round((mappedEmployees / totalEmployees) * 100) 
    : 0;

  const handleCardClick = (filter) => {
    // If clicking the already selected filter, clear it (set to null)
    if (selectedFilter === filter) {
      onFilterChange(null);
    } else {
      onFilterChange(filter);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Matrix Mapping Summary
      </Typography>
      <Grid 
        container 
        spacing={3}
        sx={{
          width: '100%',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, 1fr)'
          },
          gap: '24px',
          '& .MuiGrid-item': {
            padding: '0',
            width: '100%'
          }
        }}
      >
        <Grid item>
          <StatCard 
            icon={<PeopleIcon fontSize="large" />}
            title="Total Individuals"
            value={totalEmployees}
            color="#607D8B"
            isSelected={selectedFilter === 'all'}
            onClick={() => handleCardClick('all')}
            tooltip="Click to show all individuals"
          />
        </Grid>
        <Grid item>
          <StatCard 
            icon={<CheckCircleIcon fontSize="large" />}
            title="Mapped Individuals"
            value={mappedEmployees}
            color="#4CAF50"
            isSelected={selectedFilter === 'mapped'}
            onClick={() => handleCardClick('mapped')}
            tooltip={`${mappedPercentage}% of individuals have matching templates. Click to filter.`}
          />
        </Grid>
        <Grid item>
          <StatCard 
            icon={<ErrorIcon fontSize="large" />}
            title="Unmapped Individuals"
            value={unmappedEmployees}
            color="#F44336"
            isSelected={selectedFilter === 'unmapped'}
            onClick={() => handleCardClick('unmapped')}
            tooltip="Individuals without matching skills matrix templates. Click to filter."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

MappingSummary.propTypes = {
  totalEmployees: PropTypes.number.isRequired,
  mappedEmployees: PropTypes.number.isRequired,
  unmappedEmployees: PropTypes.number.isRequired,
  selectedFilter: PropTypes.oneOf(['all', 'mapped', 'unmapped', null]),
  onFilterChange: PropTypes.func.isRequired
};

export default MappingSummary; 